import { Zap, ZapOff } from "lucide-react";
import { type PointerEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ORBIT_TONE, TOP_UP_ARC, balanceOrbitVariants } from "./balance-tab-variants";
import { BalanceBreakdown } from "./BalanceBreakdown";
import { DeductionPulse } from "./DeductionPulse";
import { getCaptainBalanceState } from "../balance-state";
import { HEADLINE_COMPACT_FROM, useShortTokenFormat } from "./tiles/useTokenFormat";
import { useBalanceDecrease } from "../hooks/useBalanceDecrease";
import { useTopUpSpent } from "../hooks/useTopUpSpent";
import type { CaptainBillingAccount } from "../types";
import { availableShares, capResetDate, isHoverPointer } from "../utils";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/v2/popover";
import { cn } from "@/lib/utils/cn";
import { formatDayMonth } from "@/utils/dateUtils";
import { useValueFormatter } from "@/utils/useValueFormatter";

const HOVER_CLOSE_DELAY_MS = 120;
const DEDUCTION_PEEK_MS = 2400;

const ARC_TRANSITION = "transition-[stroke-dasharray,stroke-dashoffset] duration-700 ease-out motion-reduce:transition-none";

export function BalanceOrbit({ account }: { account: CaptainBillingAccount }) {
  const { t, i18n } = useTranslation();
  const { format } = useValueFormatter();
  const state = getCaptainBalanceState(account);
  const tone = ORBIT_TONE[state];
  const available = account.availableTokens;
  const decrease = useBalanceDecrease(available);
  const refillDate = formatDayMonth(capResetDate(account.month), i18n.language);
  const formatTokens = (tokens: number) => format(tokens, "decimal");
  const headlineTokens = useShortTokenFormat(HEADLINE_COMPACT_FROM);
  const usedUp = state === "usedUp";
  const hasCap = account.monthlyCapTokens > 0;
  const { spent: spentFromTopUp } = useTopUpSpent(account);
  const shares = availableShares(account, spentFromTopUp);

  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [peeking, setPeeking] = useState(false);
  const openedByHover = useRef(false);
  const pressedByHover = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!decrease) return;
    setPeeking(true);
    const timer = setTimeout(() => setPeeking(false), DEDUCTION_PEEK_MS);
    return () => clearTimeout(timer);
  }, [decrease]);

  const hoverOpen = (event: PointerEvent) => {
    if (!isHoverPointer(event)) return;
    clearTimeout(closeTimer.current);
    setHovered(true);
    if (!open) openedByHover.current = true;
    setOpen(true);
  };
  const hoverClose = (event: PointerEvent) => {
    if (!isHoverPointer(event)) return;
    closeTimer.current = setTimeout(() => {
      setHovered(false);
      setOpen(false);
    }, HOVER_CLOSE_DELAY_MS);
  };

  const revealed = open || hovered || peeking;
  const label = usedUp
    ? hasCap
      ? t("captain.topbar.orbitUsedUp", { defaultValue: "Tokens: used up, back on {{date}}", date: refillDate })
      : t("captain.topbar.orbitUsedUpNoCap", { defaultValue: "Tokens: used up, ask your administrator for a top-up" })
    : t("captain.topbar.orbitLeft", { defaultValue: "Tokens: {{tokens}} left", tokens: formatTokens(available) });
  const Glyph = usedUp ? ZapOff : Zap;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) openedByHover.current = false;
        else setHovered(false);
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          data-balance-state={state}
          data-revealed={revealed}
          onPointerEnter={hoverOpen}
          onPointerLeave={hoverClose}
          onPointerDown={(event) => {
            pressedByHover.current = isHoverPointer(event);
          }}
          onClick={(event) => {
            if (open && pressedByHover.current && event.detail > 0) event.preventDefault();
            pressedByHover.current = false;
          }}
          className={balanceOrbitVariants({ revealed })}
        >
          <span aria-hidden className="relative grid size-[38px] shrink-0 place-items-center">
            <svg viewBox="0 0 38 38" className="absolute inset-0 -rotate-90">
              <circle cx="19" cy="19" r="16" pathLength={100} fill="none" strokeWidth={2.5} className={tone.track} />
              {shares.topUp > 0 && <OrbitArc percent={shares.topUp} className={TOP_UP_ARC} />}
              {shares.cap > 0 && <OrbitArc percent={shares.cap} offset={shares.topUp} className={tone.arc} />}
            </svg>
            <Glyph className={cn("size-4", tone.glyph)} />
            {tone.dot && <span className={cn("absolute right-0.5 top-0.5 size-2 rounded-full ring-2 ring-card", tone.dot)} />}
          </span>
          <span
            aria-hidden
            data-testid="captain-orbit-reveal"
            className={cn(
              "overflow-hidden whitespace-nowrap text-sm font-semibold tabular-nums transition-[max-width,opacity,padding] duration-300 ease-out motion-reduce:transition-none",
              revealed ? "max-w-48 pr-3.5 opacity-100" : "max-w-0 opacity-0",
            )}
          >
            {usedUp ? (
              <>
                {t("captain.topbar.usedUp", { defaultValue: "Used up" })}
                <small className="ml-1 text-xs font-medium [color:var(--badge-error-text)]">
                  {hasCap
                    ? t("captain.topbar.backOn", { defaultValue: "back on {{date}}", date: refillDate })
                    : t("captain.topbar.needsTopUp", { defaultValue: "needs a top-up" })}
                </small>
              </>
            ) : (
              <>
                {headlineTokens(available)}
                <small className="ml-1 text-xs font-medium text-muted-foreground">
                  {t("captain.topbar.leftSuffix", { defaultValue: "left" })}
                </small>
              </>
            )}
          </span>
          <DeductionPulse decrease={decrease} format={formatTokens} className="rounded-full" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={10}
        aria-label={t("captain.topbar.title", { defaultValue: "Tokens" })}
        className="w-96 rounded-xl p-0 shadow-lg"
        onOpenAutoFocus={(event) => {
          if (openedByHover.current) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          if (openedByHover.current) event.preventDefault();
        }}
        onPointerEnter={hoverOpen}
        onPointerLeave={hoverClose}
      >
        <BalanceBreakdown account={account} available={available} refillDate={refillDate} state={state} />
      </PopoverContent>
    </Popover>
  );
}

function OrbitArc({ percent, offset = 0, className }: { percent: number; offset?: number; className: string }) {
  return (
    <circle
      cx="19"
      cy="19"
      r="16"
      pathLength={100}
      fill="none"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeDasharray={`${percent} 100`}
      strokeDashoffset={-offset}
      className={cn(ARC_TRANSITION, className)}
    />
  );
}
